/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module avi/clarity
 */

import {
    angleDoubleIcon,
    angleIcon,
    applicationIcon,
    arrowIcon,
    assignUserIcon,
    atomIcon,
    backupIcon,
    barChartIcon,
    bellIcon,
    betaIcon,
    boltIcon,
    bubbleExclamationIcon,
    bundleIcon,
    CdsIcon,
    certificateIcon,
    chatBubbleIcon,
    checkboxListIcon,
    checkCircleIcon,
    childArrowIcon,
    circleArrowIcon,
    circleIcon,
    ClarityIcons,
    clipboardIcon,
    cloneIcon,
    cloudIcon,
    cloudScaleIcon,
    cloudTrafficIcon,
    codeIcon,
    cogIcon,
    compassIcon,
    copyToClipboardIcon,
    cursorHandIcon,
    dashboardIcon,
    detailsIcon,
    displayIcon,
    downloadIcon,
    dragHandleIcon,
    ellipsisVerticalIcon,
    employeeIcon,
    envelopeIcon,
    eventIcon,
    exclamationCircleIcon,
    exclamationTriangleIcon,
    eyeIcon,
    factoryIcon,
    fileGroupIcon,
    fileIcon,
    fileSettingsIcon,
    fileShareIcon,
    filter2Icon,
    firewallIcon,
    folderIcon,
    formIcon,
    heartIcon,
    helpIcon,
    helpInfoIcon,
    hostGroupIcon,
    hostIcon,
    idBadgeIcon,
    infoCircleIcon,
    infoStandardIcon,
    keyIcon,
    layersIcon,
    lightbulbIcon,
    lineChartIcon,
    linkIcon,
    listIcon,
    lockIcon,
    mapMarkerIcon,
    minusCircleIcon,
    minusIcon,
    networkGlobeIcon,
    networkSettingsIcon,
    networkSwitchIcon,
    noAccessIcon,
    organizationIcon,
    paperclipIcon,
    pencilIcon,
    playIcon,
    plusCircleIcon,
    popOutIcon,
    powerIcon,
    printerIcon,
    rackServerIcon,
    refreshIcon,
    resourcePoolIcon,
    searchIcon,
    shareIcon,
    shieldCheckIcon,
    shieldIcon,
    sliderIcon,
    starIcon,
    stepForward2Icon,
    stopIcon,
    switchIcon,
    tabletIcon,
    tagIcon,
    tasksIcon,
    thumbsUpIcon,
    timesCircleIcon,
    timesIcon,
    trashIcon,
    truckIcon,
    twoWayArrowsIcon,
    unknownStatusIcon,
    userIcon,
    viewColumnsIcon,
    viewListIcon,
    vmBugIcon,
    vmwAppIcon,
    warningStandardIcon,
    windowCloseIcon,
    worldIcon,
    wrenchIcon,
    zoomInIcon,
    zoomOutIcon,
} from '@cds/core/icon';

import { internetOfThingsIcon } from '@cds/core/icon/shapes/internet-of-things';

import {
    botIcon,
    cloudProxy,
    loadBalancerIcon,
    operationsIcon,
    recommendationIcon,
    renewCertificateIcon,
    solidStarIcon,
} from './custom-svg-icons';

const iconList = [
    angleDoubleIcon,
    angleIcon,
    applicationIcon,
    arrowIcon,
    assignUserIcon,
    atomIcon,
    backupIcon,
    barChartIcon,
    bellIcon,
    betaIcon,
    boltIcon,
    bubbleExclamationIcon,
    bundleIcon,
    certificateIcon,
    chatBubbleIcon,
    checkboxListIcon,
    checkCircleIcon,
    childArrowIcon,
    circleArrowIcon,
    circleIcon,
    clipboardIcon,
    cloneIcon,
    cloudIcon,
    cloudScaleIcon,
    cloudTrafficIcon,
    codeIcon,
    cogIcon,
    compassIcon,
    copyToClipboardIcon,
    cursorHandIcon,
    dashboardIcon,
    detailsIcon,
    displayIcon,
    downloadIcon,
    dragHandleIcon,
    ellipsisVerticalIcon,
    employeeIcon,
    envelopeIcon,
    eventIcon,
    exclamationCircleIcon,
    exclamationTriangleIcon,
    eyeIcon,
    factoryIcon,
    fileIcon,
    fileGroupIcon,
    fileSettingsIcon,
    fileShareIcon,
    filter2Icon,
    firewallIcon,
    folderIcon,
    formIcon,
    heartIcon,
    helpIcon,
    helpInfoIcon,
    hostGroupIcon,
    hostIcon,
    idBadgeIcon,
    infoCircleIcon,
    infoStandardIcon,
    internetOfThingsIcon,
    keyIcon,
    layersIcon,
    lightbulbIcon,
    lineChartIcon,
    linkIcon,
    listIcon,
    lockIcon,
    mapMarkerIcon,
    minusCircleIcon,
    minusIcon,
    networkGlobeIcon,
    networkSettingsIcon,
    networkSwitchIcon,
    noAccessIcon,
    organizationIcon,
    paperclipIcon,
    pencilIcon,
    playIcon,
    plusCircleIcon,
    popOutIcon,
    printerIcon,
    rackServerIcon,
    refreshIcon,
    resourcePoolIcon,
    searchIcon,
    shareIcon,
    shieldCheckIcon,
    shieldIcon,
    sliderIcon,
    solidStarIcon,
    starIcon,
    stepForward2Icon,
    stopIcon,
    switchIcon,
    tabletIcon,
    tagIcon,
    tasksIcon,
    thumbsUpIcon,
    timesCircleIcon,
    timesIcon,
    trashIcon,
    truckIcon,
    twoWayArrowsIcon,
    unknownStatusIcon,
    userIcon,
    viewColumnsIcon,
    viewListIcon,
    vmBugIcon,
    vmwAppIcon,
    warningStandardIcon,
    windowCloseIcon,
    wrenchIcon,
    worldIcon,
    zoomInIcon,
    zoomOutIcon,
    powerIcon,
];

// for svg icons that are not defined by Clarity
const customIconList = [
    botIcon,
    cloudProxy,
    loadBalancerIcon,
    operationsIcon,
    recommendationIcon,
    renewCertificateIcon,
];

ClarityIcons.addIcons(...iconList);

// Add custom svg icons
ClarityIcons.addIcons(...customIconList);

// Some clr-angular (clr-stepper, ...) components use clr-icon internally,
// Since we no longer use clr-icon package,
// creating a backward compatible clr-icon alias.

const directions = new Set(['up', 'down', 'left', 'right']);

// Some clr-angular components will add clr-icons with extra params in the shape attribute, like
// <clr-icon shape="angle left"></clr-icon> instead of
// <clr-icon shape="angle" direction="left"></clr-icon>. This is incompatible with
// customElements.define('clr-icon', LegacyIcon) as it expects separate attributes. In order to fix
// this, we override the shape getter and setter to split up the attribuets.
class LegacyIcon extends CdsIcon {
    public get shape(): string {
        return super.shape;
    }

    public set shape(val: string) {
        const [shape, ...shapeProps] = val.split(' ');

        if (!shapeProps.length) {
            super.shape = val;
        } else {
            this.setAttribute('shape', shape);

            const direction = shapeProps.find(prop => directions.has(prop));

            if (direction) {
                this.setAttribute('direction', direction);
            }
        }
    }
}

customElements.define('clr-icon', LegacyIcon);
